import { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Label from 'components/Label'
import Input from 'components/Input'
import Checkbox from 'components/Checkbox'
import Type from 'components/Type'
import countries from 'i18n-iso-countries'
import subdivisions from 'data/subdivisions.json'
import { find } from 'lodash'
import { renderBoldText } from 'utils/helpers'
import { 
  SET_USER,
  REQUEST_GENERAL_VC,
  REQUEST_VIATOR_VC,
  SEND_USER_POINTS,
  BOOK_HOLIBOB,
  BOOK_HOTEL_PLANNER,
  BOOK_INSURANCE,
  BOOK_TRAVEL_CURIOUS,
} from 'api/constants'
import { useTypeLoad } from 'utils/hooks'

countries.registerLocale(require('i18n-iso-countries/langs/en.json'))

const I18nAddressForm = ({
  lockCountry,
  onChange,
}) => {
  const dispatch = useDispatch()
  const user = useSelector(({ global }) => global.user)
  const userAddress = user.addresses?.[0] || {}
  const [values, setValues] = useState({
    countryCode: lockCountry || userAddress.country_name || 'US',
    city: userAddress.city || '',
    stateProvince: userAddress.state_province_code || '',
    address: userAddress.address_1 || '',
    postalCode: userAddress.postal_code || '',
  })
  const [inputValueCountry, setInputValueCountry] = useState('')
  const [inputValueSubdivision, setInputValueSubdivision] = useState('')
  const [saveAddress, setSaveAddress] = useState(false)

  const countryList = useMemo(() => {
    return Object.entries(countries.getNames('en'))
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => (a.code === 'US' ? -1 : b.code === 'US' ? 1 : 0))
  }, [])

  const subdivisionList = useMemo(() => {
    return subdivisions?.[values.countryCode] || []
  }, [values.countryCode])

  const handleOnChange = data => {
    setValues(prev => ({
      ...prev,
      ...data,
    }))
  }

  const handleSaveAddress = () => {
    if (saveAddress && !find(user.addresses, { billing_address_1: values.address })) {
      dispatch({
        type: SET_USER,
        data: {
          billing_address_1: values.address,
          // billing_address_2: '',
          // billing_address_3: '',
          billing_city: values.city,
          billing_state: values.stateProvince,
          billing_zip: values.postalCode,
          billing_country: values.countryCode,
        },
      })
    }
  }

  // TODO: figure out a better way to save on dismount
  useTypeLoad(REQUEST_GENERAL_VC, data => {
    if (!data.error) handleSaveAddress()
  })
  useTypeLoad(REQUEST_VIATOR_VC, data => {
    if (!data.error) handleSaveAddress()
  })
  useTypeLoad(SEND_USER_POINTS, data => {
    if (!data.error) handleSaveAddress()
  })
  useTypeLoad(BOOK_HOLIBOB, data => {
    if (!data.error) handleSaveAddress()
  })
  useTypeLoad(BOOK_HOTEL_PLANNER, data => {
    if (!data.error) handleSaveAddress()
  })
  useTypeLoad(BOOK_INSURANCE, data => {
    if (!data.error) handleSaveAddress()
  })
  useTypeLoad(BOOK_TRAVEL_CURIOUS, data => {
    if (!data.error) handleSaveAddress()
  })

  useEffect(() => {
    if (
      values.countryCode.length &&
      values.city.length &&
      values.stateProvince.length &&
      values.address.length &&
      values.postalCode.length
    ) {
      onChange?.(values)
    } else {
      onChange?.({
        countryCode: undefined,
        city: undefined,
        stateProvince: undefined,
        address: undefined,
        postalCode: undefined,
      })
    }
  }, [values])
  
  // const data = {
  //   address: values.address || userAddress.address_1 || '',
  //   city: values.city || userAddress.city || '',
  //   stateProvince: values.stateProvince || userAddress.state_province_code || '',
  //   countryCode: lockCountry || values.countryCode || userAddress.country_name || '',
  //   postalCode: values.postalCode || userAddress.postal_code || '',
  // }

  return (
    <Stack
      gap={2}
      // mb={5}
      // maxWidth={500}
    >
      <Input 
        label="Street Address"
        autocomplete="address-level3"
        value={values.address}
        onChange={data => handleOnChange({ address: data })}
      />
      <Stack
        gap={1}
      >
        <Label>
          Country
        </Label>
        <Autocomplete
          className="outlined"
          // autocomplete="country"
          autoHighlight
          autoSelect
          disabled={!!lockCountry}
          options={countryList}
          getOptionLabel={option => option.name}
          renderOption={(props, option) => (
            <div
              {...props}
            >
              <Type
                variant="dataSmall"
              >
                {renderBoldText(option.name, inputValueCountry)}
              </Type>
            </div>
          )}
          value={countryList.find(option => option.code === values.countryCode)}
          onChange={(e, newValue) => {
            handleOnChange({ countryCode: newValue?.code || '', stateProvince: '' })
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              variant="filled" 
              fullWidth 
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
            )}
          onInputChange={(e, newInputValue) => setInputValueCountry(newInputValue?.length ? newInputValue : '')}
        />
      </Stack>
      <Stack
        gap={1}
      >
        <Label>
          State / Province / Region
        </Label>
        <Autocomplete
          className="outlined"
          autoHighlight
          autoSelect
          disabled={!!!values.countryCode?.length}
          freeSolo={!!!subdivisionList?.length}
          options={subdivisionList}
          getOptionLabel={(option) => option?.name || option || ''}
          renderOption={(props, option) => (
            <div
              {...props}
            >
              <Type
                variant="dataSmall"
              >
                {renderBoldText(option.name, inputValueSubdivision)}
              </Type>
            </div>
          )}
          value={subdivisionList.find(option => option.code === values.stateProvince) || values.stateProvince}
          onChange={(e, newValue) => {
            handleOnChange({ stateProvince: newValue?.code || newValue || '' })
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              variant="filled" 
              fullWidth 
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
          )}
          onInputChange={(e, newInputValue) => setInputValueSubdivision(newInputValue?.length ? newInputValue : '')}
        />
      </Stack>
      <Stack
        display="grid"
        gap={1}
        gridTemplateColumns={{
          sm: '1fr 150px',
        }}
      >
        <Input 
          label="City"
          autocomplete="address-level2"
          value={values.city}
          onChange={data => handleOnChange({ city: data })}
        />
        <Input 
          label="Zip / Postal Code"
          required
          {...(values.countryCode === 'US' ? {
            mask: 'postal_code',
            autocomplete: 'postal-code',
          } : {})}
          value={values.postalCode}
          onChange={data => handleOnChange({ postalCode: data })}
        />
      </Stack>
      {!!!user.addresses?.length && (
        <Checkbox
          label="Save billing address for future checkout"
          labelColor="secondary"
          checked={saveAddress}
          onChange={() => setSaveAddress(prev => !prev)}
        />
      )}
    </Stack>
  )
}

export default I18nAddressForm
